import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal, Button, Typography, Space, message } from 'antd';
import { DownloadOutlined, PrinterOutlined, CloseOutlined, ArrowLeftOutlined, ArrowRightOutlined, MailOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { SimplifiedPendingOrder } from '@/services/PendingOrdersService';
import PendingOrderDocument from '@/components/orders/PendingOrderDocument';
import OrderEmailPanel from '@/components/orders/OrderEmailPanel';
import { buildPdfBase64FromElement, buildPdfFromElement } from '@/components/orders/pdfUtils';

const { Text } = Typography;

interface PendingOrdersPDFModalProps {
  visible: boolean;
  onClose: () => void;
  order: SimplifiedPendingOrder | null;
  orders?: SimplifiedPendingOrder[];
  currentIndex?: number;
  onNavigate?: (index: number) => void;
  title?: string;
}

const PendingOrdersPDFModal: React.FC<PendingOrdersPDFModalProps> = ({
  visible,
  onClose,
  order,
  orders = [],
  currentIndex = 0,
  onNavigate,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const canGoPrevious = useMemo(() => currentIndex > 0, [currentIndex]);
  const canGoNext = useMemo(() => currentIndex < orders.length - 1, [currentIndex, orders.length]);

  const handlePrevious = useCallback(() => {
    if (canGoPrevious && onNavigate) onNavigate(currentIndex - 1);
  }, [currentIndex, canGoPrevious, onNavigate]);

  const handleNext = useCallback(() => {
    if (canGoNext && onNavigate) onNavigate(currentIndex + 1);
  }, [currentIndex, canGoNext, onNavigate]);

  const handleDownloadPDF = useCallback(async () => {
    if (!contentRef.current || !order) return;
    try {
      message.loading({ content: 'Generando PDF...', key: 'pdf' });
      const pdf = await buildPdfFromElement(contentRef.current, { profile: 'download' });
      pdf.save(`pedido_${order.orderNumber}_${dayjs().format('YYYY-MM-DD')}.pdf`);
      message.success({ content: 'PDF descargado', key: 'pdf' });
    } catch (error) {
      message.error({ content: 'Error al generar el PDF', key: 'pdf' });
    }
  }, [order]);

  const handleGeneratePdfBase64 = useCallback(async (): Promise<{ base64: string; filename: string }> => {
    if (!contentRef.current || !order) throw new Error('No hay pedido seleccionado');
    const base64 = await buildPdfBase64FromElement(contentRef.current, { profile: 'email' });
    return {
      base64,
      filename: `pedido_${order.orderNumber}_${dayjs().format('YYYY-MM-DD')}.pdf`,
    };
  }, [order]);

  const handlePrint = useCallback(() => {
    if (!contentRef.current || !order) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      message.error('No se pudo abrir la ventana de impresion');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pedido ${order.orderNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #000; font-size: 12px; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #333; padding: 6px 10px; text-align: left; }
          th { background: #f0f0f0; font-weight: 600; }
          @media print { body { padding: 10px; } @page { margin: 10mm; } }
        </style>
      </head>
      <body>${contentRef.current.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  }, [order]);

  const [emailModalOpen, setEmailModalOpen] = useState(false);

  if (!order) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width="95vw"
      centered
      closable={false}
      footer={null}
      styles={{
        body: {
          padding: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: '#e8e8e8',
        },
        content: { borderRadius: 8},
      }}
      style={{ maxWidth: 1000, top: 30 }}
    >
      <div
        style={{
          padding: '10px 20px',
          borderBottom: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Space size={16}>
          <Button icon={<ArrowLeftOutlined />} onClick={handlePrevious} disabled={!canGoPrevious}>
            Anterior
          </Button>
          <Text strong style={{ fontSize: 16, minWidth: 150, textAlign: 'center' }}>
            {orders.length > 0 ? `${currentIndex + 1} de ${orders.length}` : 'Sin pedidos'}
          </Text>
          <Button icon={<ArrowRightOutlined />} onClick={handleNext} disabled={!canGoNext}>
            Siguiente
          </Button>
        </Space>
        <Space size={10}>
          <Button icon={<MailOutlined />} onClick={() => setEmailModalOpen(true)}>
            Enviar por correo
          </Button>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Imprimir
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadPDF}>
            Descargar PDF
          </Button>
          <Button icon={<CloseOutlined />} onClick={onClose} />
        </Space>
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          gap: 16,
          padding: 16,
          alignItems: 'stretch',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            flex: '1 1 520px',
            minWidth: 0,
            overflow: 'auto',
            padding: 16,
            borderRadius: 10,
            border: '1px solid #e5e7eb',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div ref={contentRef}>
              <PendingOrderDocument order={order} />
            </div>
          </div>
        </div>

      </div>

      <Modal
        open={emailModalOpen}
        onCancel={() => setEmailModalOpen(false)}
        title="Enviar PDF por correo"
        footer={null}
        destroyOnHidden
        width={600}
        getContainer={() => document.body}
        zIndex={1500}
        centered
      >
        <OrderEmailPanel order={order} visible={emailModalOpen} onGeneratePdfBase64={handleGeneratePdfBase64} />
      </Modal>
    </Modal>
  );
};

export default PendingOrdersPDFModal;
